import React from "react";
import { WidgetOverlay } from "./WidgetOverlay";
import { PromoCardRender, SplitLayoutRender, ShapeBurstRender, LifestyleCardRender, MenuGridModernRender, ChalkboardGridRender, BurgerComboKidsRender } from "./PolishedRenderers";
import { CanvasRenderer } from "./CanvasRenderer";

// Reads from raw image upload (File) and returns base64 data URL (capped)
export async function fileToDataUrl(file, maxSize = 1200) {
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
  // Optionally resize via canvas to reduce payload
  return await new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const ratio = Math.min(1, maxSize / Math.max(img.width, img.height));
      const w = Math.round(img.width * ratio);
      const h = Math.round(img.height * ratio);
      const c = document.createElement("canvas");
      c.width = w;
      c.height = h;
      const ctx = c.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);
      resolve(c.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

export function MenuRenderer({ config, scale = "tv" }) {
  if (!config) return null;
  // ---- New element-based canvas rendering takes precedence ----
  if (Array.isArray(config.elements) && config.elements.length > 0) {
    return (
      <div className="w-full h-full relative">
        <CanvasRenderer
          elements={config.elements}
          background={config.background_color || "#FFFFFF"}
          canvasW={config.canvas_w || 1920}
          canvasH={config.canvas_h || 1080}
          scaleToFit
        />
      </div>
    );
  }
  const layout = config.layout || "menu";
  const sizes = scale === "tv"
    ? { title: "text-[7vw]", subtitle: "text-[2.2vw]", section: "text-[2.4vw]", item: "text-[2vw]", desc: "text-[1.2vw]", price: "text-[3vw]", footer: "text-[1.2vw]", marquee: "text-[2vw]", slideTitle: "text-[10vw]", slideSub: "text-[3vw]" }
    : { title: "text-3xl", subtitle: "text-sm", section: "text-lg", item: "text-base", desc: "text-xs", price: "text-xl", footer: "text-xs", marquee: "text-sm", slideTitle: "text-5xl", slideSub: "text-lg" };

  const bg = config.background_color || "#0a0a0a";
  const fg = config.text_color || "#fff";
  const accent = config.accent_color || "#FAFF00";
  const bgImage = config.background_image || "";
  const bgStyle = bgImage
    ? { backgroundColor: bg, backgroundImage: `linear-gradient(${bg}cc, ${bg}cc), url(${bgImage})`, backgroundSize: "cover", backgroundPosition: "center", color: fg }
    : { background: bg, color: fg };

  const renderLayout = () => {
    if (layout === "promo_card")       return <PromoCardRender config={config} />;
    if (layout === "split_layout")     return <SplitLayoutRender config={config} />;
    if (layout === "shape_burst")      return <ShapeBurstRender config={config} />;
    if (layout === "lifestyle_card")   return <LifestyleCardRender config={config} />;
    if (layout === "menu_grid_modern") return <MenuGridModernRender config={config} />;
    if (layout === "chalkboard_grid")  return <ChalkboardGridRender config={config} />;
    if (layout === "burger_combo_kids") return <BurgerComboKidsRender config={config} />;
    if (layout === "slideshow") return <SlideshowRender config={config} sizes={sizes} bg={bg} fg={fg} accent={accent} bgStyle={bgStyle} />;
    if (layout === "cards")     return <CardsRender config={config} sizes={sizes} bg={bg} fg={fg} accent={accent} bgStyle={bgStyle} />;
    if (layout === "signage_board") return <SignageBoardRender config={config} sizes={sizes} bg={bg} fg={fg} accent={accent} />;
    if (layout === "deal_board")    return <DealBoardRender config={config} sizes={sizes} bg={bg} fg={fg} accent={accent} />;
    if (layout === "image") return <ImageRender config={config} bg={bg} fg={fg} accent={accent} />;
    return <DefaultMenuRender config={config} sizes={sizes} bg={bg} fg={fg} accent={accent} bgImage={bgImage} bgStyle={bgStyle} />;
  };

  return (
    <div className="w-full h-full relative" data-testid="menu-renderer-root">
      {renderLayout()}
      <WidgetOverlay widgets={config.widgets} theme={{ bg, fg, accent }} />
    </div>
  );
}

function ImageRender({ config, bg, fg, accent }) {
  const fitMode = config.image_fit || "blur";
  const scale = Math.max(1, Math.min(3, Number(config.image_scale) || 1));
  const fgClass =
    fitMode === "cover" ? "object-cover" :
    fitMode === "stretch" ? "object-fill" :
    "object-contain";
  return (
    <div className="w-full h-full overflow-hidden relative" style={{ background: bg }}>
      {config.background_image ? (
        <>
          {fitMode === "blur" && (
            <img
              src={config.background_image}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover scale-110"
              style={{ filter: "blur(40px) brightness(0.85)" }}
            />
          )}
          <img
            src={config.background_image}
            alt={config.title || ""}
            className={`absolute inset-0 w-full h-full ${fgClass}`}
            style={{ transform: scale !== 1 ? `scale(${scale})` : undefined, transformOrigin: "center" }}
            data-testid="canva-image"
          />
        </>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-center p-[5vw]" style={{ color: fg }}>
          <div>
            <div className="font-display" style={{ fontSize: "5vw", color: accent }}>Upload your design</div>
            <div className="font-body opacity-70 mt-[1vw]" style={{ fontSize: "1.5vw" }}>Use the Background image field to upload a 1920×1080 PNG/JPG from Canva.</div>
          </div>
        </div>
      )}
    </div>
  );
}

function DefaultMenuRender({ config, sizes, bg, fg, accent, bgImage, bgStyle }) {
  const layout = config.layout || "menu";
  const isRetail = layout === "retail";
  return (
    <div className="w-full h-full overflow-hidden flex flex-col p-[3vw] gap-[2vw]" style={bgStyle}>
      <header className="flex items-end justify-between gap-6 border-b pb-[2vw]" style={{ borderColor: `${fg}22` }}>
        <div>
          {config.logo_url && (
            <img src={config.logo_url} alt="logo" className="h-[5vw] max-h-20 mb-[1vw] object-contain" />
          )}
          <h1 className={`font-display ${sizes.title} leading-none tracking-tight`}>{config.title}</h1>
          {config.subtitle && <div className={`${sizes.subtitle} opacity-70 mt-2 font-body`}>{config.subtitle}</div>}
        </div>
        <div className="text-right">
          <div className={`${sizes.subtitle} font-bold uppercase tracking-widest`} style={{ color: accent }}>{new Date().toLocaleDateString(undefined, { weekday: "long" })}</div>
          <div className={`${sizes.subtitle} opacity-70 font-body`}>{new Date().toLocaleDateString()}</div>
        </div>
      </header>

      <div className={`flex-1 grid ${isRetail ? "grid-cols-3" : "grid-cols-1 md:grid-cols-2"} gap-[3vw] overflow-hidden`}>
        {(config.sections || []).flatMap((sec) =>
          isRetail
            ? (sec.items || []).map((it) => (
                <div key={it.id} className="flex flex-col items-center justify-center text-center border" style={{ borderColor: `${fg}22`, padding: "2vw" }}>
                  {it.image_url && <img src={it.image_url} alt="" className="h-[10vw] w-full object-cover mb-[1vw]" />}
                  <div className={`${sizes.item} font-bold leading-tight`}>{it.name}</div>
                  <div className={`${sizes.desc} opacity-70 mb-[1vw]`}>{it.description}</div>
                  <div className={`font-display leading-none`} style={{ color: accent, fontSize: "6vw" }}>{it.price?.toString().includes("$") ? it.price : `$${it.price}`}</div>
                </div>
              ))
            : [(
                <section key={sec.id} className="flex flex-col gap-[1.5vw]">
                  <h2 className={`font-display ${sizes.section} tracking-widest uppercase border-b pb-[0.6vw]`} style={{ color: accent, borderColor: `${accent}55` }}>{sec.name}</h2>
                  <div className="flex flex-col gap-[1.2vw]">
                    {(sec.items || []).map((it) => (
                      <div key={it.id} className="flex items-start gap-4">
                        {it.image_url && <img src={it.image_url} alt="" className="h-[5vw] w-[5vw] object-cover" />}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-3">
                            <div className={`${sizes.item} font-bold leading-tight truncate`}>{it.name}</div>
                            <div className={`font-display ${sizes.price} leading-none`} style={{ color: accent }}>{it.price?.toString().includes("$") ? it.price : `$${it.price}`}</div>
                          </div>
                          {it.description && <div className={`${sizes.desc} opacity-70 mt-1 font-body`}>{it.description}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )]
        )}
      </div>

      {config.footer_text && (
        <footer className={`${sizes.footer} text-center opacity-60 border-t pt-[1vw] font-body`} style={{ borderColor: `${fg}22` }}>
          {config.footer_text}
        </footer>
      )}
    </div>
  );
}

function SlideshowRender({ config, sizes, bg, fg, accent, bgStyle }) {
  const slides = config.slides || [];
  const [idx, setIdx] = React.useState(0);
  React.useEffect(() => {
    if (slides.length === 0) return;
    const dur = (slides[idx]?.duration_seconds || 6) * 1000;
    const t = setTimeout(() => setIdx((i) => (i + 1) % slides.length), dur);
    return () => clearTimeout(t);
  }, [idx, slides]);

  if (slides.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center" style={bgStyle || { background: bg, color: fg }}>
        <div className="font-display text-6xl">{config.title || "No slides"}</div>
      </div>
    );
  }

  return (
    <div className="w-full h-full relative overflow-hidden" style={bgStyle || { background: bg, color: fg }}>
      {slides.map((s, i) => (
        <div key={s.id} className="absolute inset-0 transition-opacity duration-700" style={{ opacity: i === idx ? 1 : 0 }}>
          {s.image_url && <img src={s.image_url} alt="" className="w-full h-full object-cover" />}
          <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.75), rgba(0,0,0,0.1) 50%, rgba(0,0,0,0.4))" }} />
          <div className="absolute inset-0 flex flex-col justify-end p-[5vw]">
            {s.subtitle && <div className={`${sizes.slideSub} mb-[1vw] font-bold uppercase tracking-widest`} style={{ color: accent }}>{s.subtitle}</div>}
            <div className={`font-display ${sizes.slideTitle} leading-[0.9] tracking-tighter`}>{s.title}</div>
          </div>
        </div>
      ))}
      {config.marquee_text && (
        <div className="absolute bottom-0 left-0 right-0 overflow-hidden border-t" style={{ borderColor: `${fg}22`, background: bg }}>
          <div className="marquee-track py-[1vw]">
            {[0, 1].map((k) => (
              <span key={k} className={`${sizes.marquee} font-bold uppercase tracking-widest px-12 inline-block`}>{config.marquee_text}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Image-rich card grid layout — premium look with hero photos and price tags.
function CardsRender({ config, sizes, bg, fg, accent, bgStyle }) {
  const items = (config.sections || []).flatMap((s) => (s.items || []).map((it) => ({ ...it, _section: s.name })));
  const placeholder =
    "data:image/svg+xml;utf8," +
    encodeURIComponent(
      `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 300'><rect width='400' height='300' fill='${(bg || "#222").replace("#", "%23")}'/><text x='50%' y='50%' fill='${(fg || "#888").replace("#", "%23")}' opacity='0.4' font-family='sans-serif' font-size='28' text-anchor='middle' dy='.3em'>Add photo</text></svg>`
    );
  return (
    <div className="w-full h-full overflow-hidden flex flex-col" style={bgStyle}>
      {/* Big bold ribbon header */}
      <header className="flex items-stretch" style={{ background: accent }}>
        <div className="flex-1 px-[3vw] py-[1.5vw]" style={{ background: accent, color: bg }}>
          {config.logo_url && <img src={config.logo_url} alt="logo" className="h-[4vw] max-h-16 mb-[0.5vw] object-contain" />}
          <h1 className={`font-display ${sizes.title} leading-none tracking-tight`}>{config.title}</h1>
          {config.subtitle && <div className={`${sizes.subtitle} font-bold opacity-90 mt-1 font-body`}>{config.subtitle}</div>}
        </div>
        <div className="px-[3vw] py-[1.5vw] flex flex-col items-end justify-center" style={{ background: bg, color: accent }}>
          <div className={`${sizes.subtitle} font-bold uppercase tracking-widest`}>{new Date().toLocaleDateString(undefined, { weekday: "long" })}</div>
          <div className={`${sizes.subtitle} opacity-70 font-body`}>{new Date().toLocaleDateString()}</div>
        </div>
      </header>

      <div className="flex-1 px-[3vw] py-[2vw] grid grid-cols-3 auto-rows-fr gap-[1.5vw] overflow-hidden min-h-0">
        {items.slice(0, 6).map((it, idx) => (
          <div key={it.id} className="relative overflow-hidden flex flex-col min-h-0" style={{ background: bg, color: fg }}>
            <div className="relative w-full" style={{ flex: "0 0 55%" }}>
              <img src={it.image_url || placeholder} alt={it.name} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute top-[0.8vw] left-[0.8vw] flex items-center justify-center font-display leading-none shadow-lg" style={{ width: "3vw", height: "3vw", background: accent, color: "#fff", fontSize: "1.9vw", borderRadius: "999px" }}>{idx + 1}</div>
            </div>
            <div className="flex-1 flex flex-col items-start justify-between px-[1vw] py-[0.6vw] min-h-0">
              <div className="w-full min-w-0">
                <div className="font-display leading-tight" style={{ color: accent, fontSize: "1.8vw" }}>{it.name}</div>
                {it.description && <div className="opacity-70 mt-[0.2vw] font-body line-clamp-1" style={{ fontSize: "1.1vw" }}>{it.description}</div>}
              </div>
              {it.price && (
                <div className="font-display leading-none mt-[0.2vw]" style={{ color: fg, fontSize: "2.3vw" }}>{String(it.price).includes("$") ? it.price : `$${it.price}`}</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {config.footer_text && (
        <footer className={`${sizes.footer} text-center px-[3vw] py-[1vw] font-body font-bold`} style={{ background: accent, color: bg }}>
          {config.footer_text}
        </footer>
      )}
    </div>
  );
}

// =============================================================================
// Signage Board — chalkboard-style with photo tiles + slanted price tags.
// Modeled after high-conversion digital food signage (multi-column grid).
// =============================================================================
function SignageBoardRender({ config, sizes, bg, fg, accent }) {
  const sections = config.sections || [];
  const allItems = sections.flatMap((s) => (s.items || []).map((it) => ({ ...it, _section: s.name })));
  // Chalkboard gradient with subtle vignette + grain
  const boardBg = {
    background: `radial-gradient(ellipse at center, ${bg} 0%, #000 100%)`,
    color: fg,
  };
  return (
    <div className="w-full h-full overflow-hidden flex flex-col relative" style={boardBg}>
      {/* Subtle marble noise */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.04) 0, transparent 50%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.03) 0, transparent 60%)",
        }}
      />
      {/* Header */}
      <header className="relative px-[3vw] pt-[2.5vw] pb-[1.5vw] flex items-end justify-between gap-6">
        <div>
          {config.logo_url && <img src={config.logo_url} alt="logo" className="h-[4.5vw] max-h-20 mb-[0.8vw] object-contain" />}
          <h1 className="font-display tracking-tighter leading-[0.9] uppercase" style={{ fontSize: "6.5vw" }}>
            {(config.title || "").split(" ").map((w, i, arr) => (
              <span key={i} style={{ color: i === arr.length - 1 && arr.length > 1 ? accent : fg }}>{w}{i < arr.length - 1 ? " " : ""}</span>
            ))}
          </h1>
          {config.subtitle && <div className="opacity-70 mt-[0.6vw] font-body" style={{ fontSize: "1.6vw" }}>{config.subtitle}</div>}
        </div>
        <div
          className="text-right font-body uppercase tracking-widest px-[1.5vw] py-[0.8vw]"
          style={{ background: accent, color: "#0a0a0a", fontSize: "1.3vw", fontWeight: 800, transform: "rotate(-2deg)" }}
        >
          <div>{new Date().toLocaleDateString(undefined, { weekday: "long" })}</div>
          <div className="opacity-80">{new Date().toLocaleDateString()}</div>
        </div>
      </header>

      {/* Yellow divider line */}
      <div className="relative mx-[3vw]" style={{ height: "3px", background: accent }} />

      {/* Grid of photo tiles with slanted price tags */}
      <div className="relative flex-1 grid gap-[1.5vw] px-[3vw] py-[2vw] overflow-hidden min-h-0" style={{
        gridTemplateColumns: `repeat(${Math.min(Math.max(allItems.length, 4), 7)}, minmax(0, 1fr))`,
      }}>
        {allItems.slice(0, 7).map((it) => (
          <div key={it.id} className="relative flex flex-col items-center text-center min-h-0">
            {/* Name */}
            <div className="font-display uppercase tracking-tight leading-[0.95] mb-[0.6vw]" style={{ fontSize: "1.6vw", color: fg }}>
              {(it.name || "").split(" ").map((w, i, arr) => (
                <React.Fragment key={i}>
                  <span style={{ color: i === arr.length - 1 && arr.length > 1 ? accent : fg }}>{w}</span>
                  {i < arr.length - 1 && <br/>}
                </React.Fragment>
              ))}
            </div>
            {/* Slanted yellow price tag */}
            {it.price && (
              <div
                className="font-display leading-none mb-[1vw] shadow-lg"
                style={{
                  background: accent,
                  color: "#0a0a0a",
                  padding: "0.5vw 1.2vw",
                  fontSize: "2.2vw",
                  transform: "skew(-12deg)",
                  fontWeight: 800,
                  letterSpacing: "0.02em",
                }}
              >
                <span style={{ display: "inline-block", transform: "skew(12deg)" }}>
                  {String(it.price).match(/^[0-9.]/) ? `$${it.price}` : it.price}
                </span>
              </div>
            )}
            {/* Photo */}
            {it.image_url ? (
              <div className="relative w-full" style={{ aspectRatio: "1 / 1" }}>
                <img
                  src={it.image_url}
                  alt={it.name}
                  className="absolute inset-0 w-full h-full object-contain drop-shadow-2xl"
                  style={{ filter: "drop-shadow(0 8px 16px rgba(0,0,0,0.5))" }}
                />
              </div>
            ) : (
              <div className="w-full flex items-center justify-center opacity-30" style={{ aspectRatio: "1 / 1", fontSize: "1vw" }}>
                <span className="font-body uppercase tracking-widest">Add photo</span>
              </div>
            )}
            {it.description && (
              <div className="font-body opacity-60 mt-[0.4vw] line-clamp-2" style={{ fontSize: "0.95vw" }}>{it.description}</div>
            )}
          </div>
        ))}
      </div>

      {/* Footer ribbon */}
      {config.footer_text && (
        <footer className="relative px-[3vw] py-[1vw] font-body font-bold uppercase tracking-widest text-center" style={{ background: accent, color: "#0a0a0a", fontSize: "1.2vw" }}>
          {config.footer_text}
        </footer>
      )}
    </div>
  );
}

// =============================================================================
// Deal Board — left panel with hero "Box Deals", right panel with scrollable list.
// Modeled after chicken-shop combo/deal boards.
// =============================================================================
function DealBoardRender({ config, sizes, bg, fg, accent }) {
  const sections = config.sections || [];
  // First section = hero deals (left), remaining sections = right-side list
  const heroSection = sections[0];
  const heroItems = (heroSection?.items || []).slice(0, 4);
  const listSections = sections.slice(1);

  return (
    <div className="w-full h-full overflow-hidden grid grid-cols-2" style={{ background: bg, color: fg }}>
      {/* Left: hero deals with numbered badges */}
      <div className="relative flex flex-col p-[2.5vw] overflow-hidden" style={{ background: bg }}>
        {/* Subtle gradient overlay */}
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: `linear-gradient(135deg, ${bg} 0%, transparent 60%)` }} />
        <div className="relative">
          {config.logo_url && <img src={config.logo_url} alt="logo" className="h-[4vw] max-h-16 mb-[0.6vw] object-contain" />}
          <h1 className="font-display uppercase tracking-tighter leading-[0.9]" style={{ fontSize: "5vw" }}>
            <span style={{ color: fg }}>{(config.title || "BOX").split(" ")[0]}</span>{" "}
            <span style={{ color: accent }}>{(config.title || "DEALS").split(" ").slice(1).join(" ") || "DEALS"}</span>
          </h1>
          {config.subtitle && <div className="font-body italic opacity-80 mt-[0.4vw]" style={{ fontSize: "1.5vw" }}>{config.subtitle}</div>}
        </div>

        <div className="relative mt-[2vw] flex-1 flex flex-col gap-[1.2vw] min-h-0 overflow-hidden">
          {heroItems.map((it, idx) => (
            <div key={it.id} className="flex items-center gap-[1.2vw]">
              <div
                className="flex-shrink-0 flex items-center justify-center font-display font-extrabold"
                style={{
                  width: "3vw", height: "3vw", background: accent, color: "#0a0a0a", borderRadius: "999px",
                  fontSize: "1.5vw",
                }}
              >
                {String(idx + 1).padStart(2, "0")}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display uppercase tracking-tight leading-tight" style={{ fontSize: "1.8vw", color: accent }}>{it.name}</div>
                {it.description && (
                  <div className="font-body opacity-70 line-clamp-2" style={{ fontSize: "1.05vw" }}>{it.description}</div>
                )}
              </div>
              <div className="font-display font-extrabold flex-shrink-0" style={{ fontSize: "2.2vw", color: fg }}>
                {String(it.price).match(/^[0-9.]/) ? `$${it.price}` : it.price}
              </div>
              {it.image_url && (
                <img src={it.image_url} alt="" className="w-[6vw] h-[6vw] object-cover flex-shrink-0" style={{ borderRadius: "0.5vw" }} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Right: list sections (à la donuts/waffles board) */}
      <div className="relative flex flex-col p-[2vw] overflow-hidden" style={{ background: "#fff", color: "#0a0a0a" }}>
        <div className="grid grid-cols-2 gap-[1.5vw] flex-1 min-h-0 overflow-hidden">
          {listSections.slice(0, 4).map((sec) => (
            <div key={sec.id} className="flex flex-col min-h-0">
              <div className="font-display uppercase tracking-tight pb-[0.4vw] border-b" style={{ color: accent, borderColor: accent, fontSize: "1.8vw" }}>
                {sec.name}
              </div>
              <div className="flex-1 flex flex-col gap-[0.4vw] mt-[0.5vw] overflow-hidden">
                {(sec.items || []).slice(0, 7).map((it) => (
                  <div key={it.id} className="flex items-center justify-between gap-2 border-b border-dashed pb-[0.3vw]" style={{ borderColor: "#00000018" }}>
                    <div className="font-body font-bold uppercase tracking-tight truncate" style={{ fontSize: "1vw" }}>{it.name}</div>
                    <div className="font-display flex-shrink-0" style={{ fontSize: "1vw", color: "#0a0a0a" }}>
                      {String(it.price).match(/^[0-9.]/) ? `$${it.price}` : it.price}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        {config.footer_text && (
          <div className="text-center mt-[1vw] font-body font-bold uppercase tracking-widest opacity-60" style={{ fontSize: "0.95vw" }}>
            {config.footer_text}
          </div>
        )}
      </div>
    </div>
  );
}
